"use client";

import { useRef } from "react";
import { PINK_BAND, SectionHead, SectionShell } from "./kit";
import { useEnterStagger } from "./kit/motion";

const POINTS = [
  {
    title: "IPs anonymised first",
    desc: "Visitor IPs are truncated to /24 (IPv4) or /48 (IPv6) before a click is stored. GDPR-minded from day one.",
  },
  {
    title: "Never sold, never shared",
    desc: "Your visitors are not the product. No ad networks, no data brokers, no third-party pixels.",
  },
  {
    title: "Export anytime, leave cleanly",
    desc: "Read every click in your dashboard, or download the request log as CSV or JSON. Delete the workspace and its links, domains and QR codes go too.",
  },
];

/**
 * Privacy statement — the page's pink drenched beat. Statement-simple like
 * TrustStats: one bold claim, three proof chips, no loops, no GradText
 * (accent magenta has no contrast on the pink surface — tone="pink" flips
 * the head to white).
 */
export default function PrivacySection() {
  const rootRef = useRef<HTMLDivElement>(null);

  useEnterStagger(rootRef, "[data-priv-point]", { y: 24, stagger: 0.12 });

  return (
    <SectionShell
      id="ref-privacy"
      variant="tight"
      band="pink"
      decoAvoid
      className="max-md:bg-[#d70e62]"
    >
      <SectionHead
        center
        tone="pink"
        gridFill={PINK_BAND}
        eyebrow="Privacy first"
        title={<>Your clicks are yours. Full stop.</>}
        lead="Deep analytics shouldn't cost your visitors their privacy — or you your compliance story."
      />

      <div ref={rootRef} data-grid-block data-grid-fill={PINK_BAND}>
        <div className="grid gap-4 md:grid-cols-3">
          {POINTS.map((p) => (
            <div
              key={p.title}
              data-priv-point
              className="rounded-ref-lg border border-white/30 bg-white/[0.12] px-5 py-4"
            >
              <div className="flex items-center gap-2.5 text-[15.5px] font-bold text-white">
                <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-white/20 text-[11px]">
                  ✓
                </span>
                {p.title}
              </div>
              <p className="mt-2 text-[13.5px] leading-[1.6] text-white/80">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </SectionShell>
  );
}
